import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import {
  ApiError,
  forgotPassword,
  getAuthConfig,
  googleExchange,
  googleStartUrl,
  login,
  setCurrentTenant,
  setToken,
  type LoginResponse,
} from "../lib/api";

/**
 * Écran de connexion.
 *
 * V1.73 (02/11/2026) : connexion Google, en plus du mot de passe. Le bouton
 * n'apparaît que si le serveur dit que c'est ouvert (`/admin/auth/config`) :
 * inutile de proposer une porte qui n'existe pas sur ce déploiement.
 *
 * Le va-et-vient se termine ici, sur `?google=<code>` : ce code vaut une minute
 * et un seul échange, et c'est lui — pas le jeton de session — qui transite par
 * l'adresse du navigateur.
 */
export function LoginPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const from = (location.state as { from?: { pathname?: string } } | null)?.from?.pathname ?? "/";
  const [searchParams, setSearchParams] = useSearchParams();
  const reason = searchParams.get("reason"); // "idle" | "expired" (deconnexion auto)
  const googleCode = searchParams.get("google");
  const googleError = searchParams.get("google_error");
  const reasonMessage =
    reason === "idle"
      ? "Vous avez été déconnecté après une période d'inactivité. Reconnectez-vous."
      : reason === "expired"
        ? "Votre session a expiré. Reconnectez-vous."
        : null;

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(googleError);
  const [submitting, setSubmitting] = useState(false);
  const authConfig = useQuery({ queryKey: ["admin", "auth-config"], queryFn: getAuthConfig, retry: false });
  const googleEnabled = authConfig.data?.google.enabled ?? false;
  // Un code ne s'échange qu'une fois : React en mode strict monte deux fois.
  const exchanging = useRef(false);
  const [googleBusy, setGoogleBusy] = useState(Boolean(googleCode));

  /** Session ouverte : même atterrissage que la connexion par mot de passe. */
  const land = (res: LoginResponse, to: string) => {
    setToken(res.token);
    if (res.user.role !== "SUPER_ADMIN" && res.user.tenantSlugs.length > 0) {
      setCurrentTenant(res.user.tenantSlugs[0]!);
    } else {
      setCurrentTenant(null);
    }
    navigate(to, { replace: true });
  };

  // Retour de Google : on échange le code contre une session.
  useEffect(() => {
    if (!googleCode || exchanging.current) return;
    exchanging.current = true;
    void (async () => {
      try {
        const res = await googleExchange(googleCode);
        land(res, res.redirect ?? from);
      } catch (err) {
        setError(
          err instanceof ApiError
            ? err.message
            : "Connexion Google impossible. Réessayez, ou utilisez votre mot de passe.",
        );
        setGoogleBusy(false);
        // On nettoie l'adresse : un code consommé qui y reste n'apporte que de
        // la confusion au rechargement.
        setSearchParams({}, { replace: true });
      }
    })();
  }, [googleCode]); // eslint-disable-line react-hooks/exhaustive-deps
  // Sprint Y-2 : self-service password reset
  const [forgotSending, setForgotSending] = useState(false);
  const [forgotSent, setForgotSent] = useState(false);

  const onForgot = async () => {
    if (!email) {
      setError("Tapez votre email d'abord pour recevoir le lien de réinitialisation.");
      return;
    }
    setError(null);
    setForgotSending(true);
    try {
      await forgotPassword(email.trim().toLowerCase());
      setForgotSent(true);
    } catch {
      // Toujours afficher succes pour ne pas leak l'existence d'emails
      setForgotSent(true);
    } finally {
      setForgotSending(false);
    }
  };

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      // Un SUPER_ADMIN ouvre sur la vue globale "Tous les clients" (null), meme
      // s'il est membre de tenants — sinon on rouvrait toujours sur le 1er
      // (ex. EPMO) apres chaque connexion. Les autres roles, qui DOIVENT choisir
      // un client, sont positionnes par defaut sur leur premier tenant.
      land(await login(email.trim().toLowerCase(), password), from);
    } catch (err) {
      if (err instanceof ApiError) setError(err.message);
      else setError("Erreur inconnue, réessayez.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4">
      <div className="w-full max-w-md bg-white border border-[color:var(--color-alto-border)] rounded-2xl p-8 shadow-sm">
        <div className="mb-8">
          <p className="text-xs tracking-widest text-[color:var(--color-alto-muted)] uppercase">
            ALTO ONSITE
          </p>
          <h1 className="font-[family-name:var(--font-display)] text-3xl mt-1">
            Back-office
          </h1>
          <p className="text-sm text-[color:var(--color-alto-muted)] mt-2">
            Connectez-vous avec votre adresse email professionnelle.
          </p>
        </div>

        {reasonMessage && (
          <div className="mb-4 text-sm rounded-md border border-amber-200 bg-amber-50 text-amber-800 px-3 py-2">
            {reasonMessage}
          </div>
        )}

        {googleBusy && (
          <div className="mb-4 text-sm rounded-md border border-[color:var(--color-alto-border)] bg-[color:var(--color-alto-bg)] px-3 py-2">
            Connexion en cours…
          </div>
        )}

        {googleEnabled && !googleBusy && (
          <div className="mb-5">
            <a
              href={googleStartUrl(from !== "/" ? from : undefined)}
              className="flex w-full items-center justify-center gap-2.5 px-4 py-2.5 rounded-md border border-[color:var(--color-alto-border)] font-medium text-sm hover:bg-[color:var(--color-alto-bg)] transition-colors"
            >
              <svg width="18" height="18" viewBox="0 0 18 18" aria-hidden="true">
                <path
                  fill="#4285F4"
                  d="M17.64 9.2c0-.64-.06-1.25-.16-1.84H9v3.48h4.84a4.14 4.14 0 0 1-1.8 2.72v2.26h2.92c1.71-1.57 2.68-3.89 2.68-6.62Z"
                />
                <path
                  fill="#34A853"
                  d="M9 18c2.43 0 4.47-.8 5.96-2.18l-2.92-2.26c-.81.54-1.84.86-3.04.86-2.34 0-4.32-1.58-5.03-3.7H.96v2.33A8.99 8.99 0 0 0 9 18Z"
                />
                <path
                  fill="#FBBC05"
                  d="M3.97 10.72a5.4 5.4 0 0 1 0-3.44V4.96H.96a9 9 0 0 0 0 8.08l3.01-2.32Z"
                />
                <path
                  fill="#EA4335"
                  d="M9 3.58c1.32 0 2.5.45 3.44 1.35l2.58-2.59C13.46.89 11.43 0 9 0A8.99 8.99 0 0 0 .96 4.96l3.01 2.32C4.68 5.16 6.66 3.58 9 3.58Z"
                />
              </svg>
              Continuer avec Google
            </a>
            <p className="mt-2 text-center text-xs text-[color:var(--color-alto-muted)]">
              Votre compte doit déjà exister ici : Google identifie, le back-office autorise.
            </p>
            <div className="mt-4 flex items-center gap-3 text-xs text-[color:var(--color-alto-muted)]">
              <span className="h-px flex-1 bg-[color:var(--color-alto-border)]" />
              ou
              <span className="h-px flex-1 bg-[color:var(--color-alto-border)]" />
            </div>
          </div>
        )}

        <form onSubmit={onSubmit} className="space-y-4">
          <div>
            <label htmlFor="email" className="block text-sm font-medium mb-1.5">
              Email
            </label>
            <input
              id="email"
              type="email"
              autoComplete="email"
              autoFocus
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="vous@alto-onsite.com"
              className="w-full px-3 py-2 rounded-md border border-[color:var(--color-alto-border)] bg-white focus:outline-none focus:ring-2 focus:ring-[color:var(--color-alto-accent)] focus:border-transparent"
            />
          </div>

          <div>
            <label htmlFor="password" className="block text-sm font-medium mb-1.5">
              Mot de passe
            </label>
            <input
              id="password"
              type="password"
              autoComplete="current-password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full px-3 py-2 rounded-md border border-[color:var(--color-alto-border)] bg-white focus:outline-none focus:ring-2 focus:ring-[color:var(--color-alto-accent)] focus:border-transparent"
            />
          </div>

          {error && (
            <p className="text-sm text-[color:var(--color-alto-accent)]" role="alert">
              {error}
            </p>
          )}

          <button
            type="submit"
            disabled={submitting || email.length === 0 || password.length === 0}
            className="w-full px-4 py-2.5 rounded-md bg-[color:var(--color-alto-ink)] text-white font-medium transition-opacity disabled:opacity-50 hover:opacity-90"
          >
            {submitting ? "Connexion…" : "Se connecter"}
          </button>

          {/* Sprint Y-2 : self-service reset password */}
          <div className="pt-2 text-center">
            {forgotSent ? (
              <p className="text-xs text-emerald-700">
                ✓ Si cet email est connu, un lien de réinitialisation vient d'être envoyé.
              </p>
            ) : (
              <button
                type="button"
                onClick={onForgot}
                disabled={forgotSending}
                className="text-xs text-[color:var(--color-alto-muted)] hover:text-[color:var(--color-alto-accent)] underline disabled:opacity-50"
              >
                {forgotSending ? "Envoi…" : "Mot de passe oublié ?"}
              </button>
            )}
          </div>
        </form>
      </div>
    </div>
  );
}
